import type { ILogService } from "angular";
import { module } from "angular";
import type { LDClient, LDFlagSet, LDOptions } from "ldclient-js";
import { initialize } from "ldclient-js";
import { isNil, mapValues, uniqueId } from "lodash";
import type { IProfileService } from "App/Modules/Auth/Services/Profile/IProfileService";
import type { IProfile } from "App/Modules/Models/Models/IProfile";
import type { IConfig } from "App/Modules/Utils/Services/Config/IConfig";
import type { Observable, Subject } from "rxjs";
import { BehaviorSubject, from as observableFrom } from "rxjs";
import type { IFeatureFlagService } from "./IFeatureFlagService";

export class FeatureFlagService implements IFeatureFlagService {
    public static $inject = ["mmProfileService", "irConfig", "$log"];

    public ldClient: LDClient;

    public flags: LDFlagSet;

    private readonly flagsChangeSubject: Subject<LDFlagSet>;

    private readonly flagsObservableInstance: Observable<LDFlagSet>;

    constructor(
        private readonly profileService: IProfileService,
        private readonly config: IConfig,
        private readonly log: ILogService,
    ) {
        this.flags = {};

        this.flagsChangeSubject = new BehaviorSubject(this.flags);
        this.flagsObservableInstance = observableFrom(this.flagsChangeSubject);

        if (!config.launchDarklySdkKey) {
            log.info("Launch Darkly key is not provided, all features will be disabled.");

            return;
        }

        this.ldClient = initialize(config.launchDarklySdkKey, {
            anonymous: true,
            key: uniqueId(),
        });

        this.ldClient.on("change", this.updateFlags.bind(this));

        this.ldClient.on("ready", this.setFlags.bind(this));

        this.profileService.profileObservable().subscribe(this.identifyUser.bind(this));
    }

    public flagsObservable(): Observable<LDFlagSet> {
        return this.flagsObservableInstance;
    }

    public waitForLDClient(): Promise<void> {
        if (this.ldClient) {
            return this.ldClient.waitUntilReady();
        }
        return Promise.resolve();
    }

    public getFlag(name: string): boolean {
        return Boolean(this.flags[name]);
    }

    public track(message: string): void {
        return this.ldClient.track(message);
    }

    private updateFlags(flags: LDOptions) {
        Object.assign(
            this.flags,
            mapValues(flags, (v) => (v as any).current),
        );

        this.flagsChangeSubject.next(this.flags);
    }

    private setFlags() {
        this.flags = this.ldClient.allFlags();

        this.flagsChangeSubject.next(this.flags);
    }

    private identifyUser(profile: IProfile) {
        if (isNil(profile)) {
            return;
        }

        // eslint-disable-next-line
        this.ldClient.identify({
            anonymous: false,
            key: profile.email,
            custom: {
                partners: profile.partners.map((partner) => partner.id),
            },
        });
    }
}

module("mmServices").service("irFeatureFlagService", FeatureFlagService);
